import React, { useState, useEffect } from 'react';
import { Sidebar, Menu, MenuItem, SubMenu } from 'react-pro-sidebar';
import { Link, useLocation } from 'react-router-dom';
import { pages, RAGLink } from '../utilities/constants';
import { useAzureData } from '../context/AzureContext';
import 'bootstrap-icons/font/bootstrap-icons.css';
import {
  LayoutDashboard,
  Boxes,
  ChartSpline,
  SlidersVertical,
  ChevronUp,
  ChevronDown,
  ArrowUpRight,
  CircleDollarSign,
  KeyRound
} from 'lucide-react';
import expand from '../assets/icon/expand_sidebar.svg';
import collapse from '../assets/icon/collapse_sidebar.svg';
import modelIcon from '../assets/icon/analytics.svg';
import library from '../assets/icon/library.svg';
import ethics from '../assets/icon/ethics.svg';
import application from '../assets/icon/Menu Icon.svg';
import myapp from '../assets/icon/myapp.svg';
import prompt_builder from '../assets/icon/prompt_builder.svg';
import model_explorer from '../assets/icon/model_explorer.svg';
import model_library from '../assets/icon/model_library.svg';
import rag_icon from '../assets/icon/rag_greyIcon.svg';
import myrag_icon from '../assets/icon/myRagIcon.svg';
import manage_rag_icon from '../assets/icon/manageRagIcon.svg';
import './Sidebar.css';

const SidebarComponent = ({ collapsed, setCollapsed }: any) => {
  const { tempAdmin, tempOwner, appUser } = useAzureData();
  const [activeItem, setActiveItem] = useState<string>('dashboard');
  const { pathname } = useLocation();

  const isModelActive =
    pathname.startsWith('/leaderboard') ||
    pathname.startsWith('/rai') ||
    pathname.startsWith('/dashboard');
  const isAnalyticsActive =
    pathname.startsWith('/costAnalytics') ||
    pathname.startsWith('/systemanalytics') ||
    pathname.startsWith('/ethicscompliance') ||
    pathname.startsWith('/healthdashboard') ||
    pathname.startsWith('/unifiedanalytics');
  const isPromptActive =
    pathname.startsWith('/promptlibrary') || pathname.startsWith('/promptplayground');
  const isModelExplorerActive = pathname.startsWith('/model') || pathname.startsWith('/model');
  const isRagBuilderActive =
    pathname.startsWith('/ragbuilder') || pathname.startsWith('/ragbuilder');

  const activeColor = '#0066CC';
  const inactiveColor = '#838FB4';

  // All submenus closed initially
  const [openSubMenus, setOpenSubMenus] = useState<{ [key: string]: boolean }>({
    application: false,
    prompt: false,
    model: false,
    rag: false
  });

  useEffect(() => {
    const [pageinfo] = pages.filter((page) => pathname.includes(page.path));
    if (pageinfo) {
      setActiveItem(pageinfo.groupname);
    } else {
      setActiveItem('');
    }
  }, [pathname]);

  const handleClick = (item: string) => {
    setActiveItem(item);
  };

  const handleSubMenuToggle = (key: string) => {
    setOpenSubMenus((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const openRAGLink = () => {
    let url;
    switch (window.location.hostname) {
      case 'localhost':
      case 'aiflmdev.unilever.com':
      case 'bnlwe-ai04-d-930633-webapi-02.azurewebsites.net':
        url = RAGLink.dev;
        break;
      case 'aiflmuat.unilever.com':
      case 'bnlwe-ai03-q-931039-webapi-02.azurewebsites.net':
        url = RAGLink.uat;
        break;
      case 'aiflmprod.unilever.com':
      case 'bnlwe-ai01-p-930632-webapi-02.azurewebsites.net':
        url = RAGLink.prod;
        break;
      default:
        url = RAGLink.dev;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Helper function for chevron direction
  const renderChevron = (submenuKey: string, isActivePath: boolean) => {
    const color = isActivePath ? activeColor : inactiveColor;
    if (collapsed || !openSubMenus[submenuKey]) {
      return <ChevronUp size={22} strokeWidth={1.5} color={color} />;
    } else {
      return <ChevronDown size={22} strokeWidth={1.5} color={color} />;
    }
  };

  return (
    <div className={`custom-sidebar ${collapsed ? 'collapsed' : ''}`}>
      <Sidebar collapsed={collapsed} width="257px" collapsedWidth="70px">
        <Menu
          menuItemStyles={{
            button: ({ active }) => ({
              backgroundColor: active && !collapsed ? '#e6f2ff' : undefined,
              color: active ? activeColor : inactiveColor,
              fontSize: '16px',
              fontWeight: '400',
              margin: '15px',
              padding: '0px !important',
              borderRight: active && !collapsed ? `5px solid ${activeColor}` : 'none',
              borderRadius: '5px'
            })
          }}>
          {/* Application */}
          <SubMenu
            style={{ marginBottom: '2px' }}
            icon={
              <img
                src={application}
                alt="Application Icon"
                style={{
                  width: 24,
                  height: 24,
                  filter:
                    pathname.startsWith('/myapps') || pathname.startsWith('/manageapprequests')
                      ? 'invert(27%) sepia(91%) saturate(2727%) hue-rotate(197deg) brightness(96%) contrast(96%)'
                      : 'none'
                }}
              />
            }
            label={
              <span
                style={{
                  color:
                    pathname.startsWith('/myapps') || pathname.startsWith('/manageapprequests')
                      ? activeColor
                      : inactiveColor
                }}>
                {collapsed ? '' : 'Application'}
              </span>
            }
            onClick={() => handleSubMenuToggle('application')}
            suffix={renderChevron(
              'application',
              pathname.startsWith('/myapps') || pathname.startsWith('/manageapprequests')
            )}>
            <MenuItem
              icon={
                <img
                  src={myapp}
                  alt="Application Icon"
                  style={{
                    width: 16,
                    height: 16,
                    filter: pathname.startsWith('/myapps')
                      ? 'invert(27%) sepia(91%) saturate(2727%) hue-rotate(197deg) brightness(96%) contrast(96%)'
                      : 'none'
                  }}
                />
              }
              active={pathname.startsWith('/myapps')}
              component={<Link to="/myapps" />}>
              My Apps
            </MenuItem>

            {tempAdmin && (
              <MenuItem
                icon={
                  <KeyRound
                    size={16}
                    strokeWidth={1.5}
                    color={pathname.startsWith('/manageapprequests') ? activeColor : inactiveColor}
                  />
                }
                active={pathname.startsWith('/manageapprequests')}
                component={<Link to="/manageapprequests" />}>
                Manage App Requests
              </MenuItem>
            )}
          </SubMenu>

          {/* Model Explorer */}
          {tempAdmin && (
            <SubMenu
              style={{ marginBottom: '2px' }}
              icon={
                <img
                  src={model_explorer}
                  alt="Model Explorer Icon"
                  style={{
                    width: 25,
                    height: 25,
                    filter: isModelExplorerActive
                      ? 'invert(27%) sepia(91%) saturate(2727%) hue-rotate(197deg) brightness(96%) contrast(96%)'
                      : 'none'
                  }}
                />
              }
              label={
                <span style={{ color: isModelExplorerActive ? activeColor : inactiveColor }}>
                  {collapsed ? '' : 'Model Explorer'}
                </span>
              }
              onClick={() => handleSubMenuToggle('model')}
              suffix={renderChevron('model', isModelExplorerActive)}>
              <MenuItem
                icon={
                  <img
                    src={model_library}
                    alt="Library Icon"
                    style={{
                      width: 16,
                      height: 16,
                      filter:
                        pathname === '/model'
                          ? 'invert(27%) sepia(91%) saturate(2727%) hue-rotate(197deg)'
                          : 'none'
                    }}
                  />
                }
                active={pathname === '/model'}
                component={<Link to="/model" />}>
                Model Library
              </MenuItem>
            </SubMenu>
          )}
          {/* Prompt Builder */}
          {
            <SubMenu
              style={{ marginBottom: '2px' }}
              icon={
                <img
                  src={prompt_builder}
                  alt="Prompt Builder Icon"
                  style={{
                    width: 25,
                    height: 25,
                    filter: isPromptActive
                      ? 'invert(27%) sepia(91%) saturate(2727%) hue-rotate(197deg) brightness(96%) contrast(96%)'
                      : 'none'
                  }}
                />
              }
              label={
                <span style={{ color: isPromptActive ? activeColor : inactiveColor }}>
                  {collapsed ? '' : 'Prompt Builder'}
                </span>
              }
              onClick={() => handleSubMenuToggle('prompt')}
              suffix={renderChevron('prompt', isPromptActive)}>
              <MenuItem
                icon={
                  <img
                    src={library}
                    alt="Library Icon"
                    style={{
                      width: 16,
                      height: 16,
                      filter:
                        pathname === '/promptlibrary'
                          ? 'invert(27%) sepia(91%) saturate(2727%) hue-rotate(197deg)'
                          : 'none'
                    }}
                  />
                }
                active={pathname === '/promptlibrary'}
                component={<Link to="/promptlibrary" />}>
                Prompt Library
              </MenuItem>

              <MenuItem
                icon={
                  <SlidersVertical
                    size={16}
                    strokeWidth={1.5}
                    color={pathname === '/promptplayground' ? activeColor : inactiveColor}
                  />
                }
                active={pathname === '/promptplayground'}
                component={<Link to="/promptplayground" />}>
                Prompt Playground
              </MenuItem>
            </SubMenu>
          }

          {/* Analytics */}
          {(tempAdmin || tempOwner || appUser) && (
            <SubMenu
              style={{ marginBottom: '2px' }}
              icon={
                <img
                  src={modelIcon}
                  alt="Model Icon"
                  style={{
                    width: 22,
                    height: 22,
                    filter: isAnalyticsActive
                      ? 'invert(27%) sepia(91%) saturate(2727%) hue-rotate(197deg) brightness(96%) contrast(96%)'
                      : 'none'
                  }}
                />
              }
              label={
                <span style={{ color: isAnalyticsActive ? activeColor : inactiveColor }}>
                  {collapsed ? '' : 'Analytics'}
                </span>
              }
              onClick={() => handleSubMenuToggle('model')}
              suffix={renderChevron('model', isAnalyticsActive)}>
              {(tempAdmin || tempOwner || appUser) && (
                <MenuItem
                  icon={
                    <LayoutDashboard
                      size={17}
                      strokeWidth={1.5}
                      color={pathname === '/unifiedanalytics' ? activeColor : inactiveColor}
                    />
                  }
                  active={pathname === '/unifiedanalytics'}
                  component={<Link to="/unifiedanalytics" />}>
                  Unified KPI Dashboard
                </MenuItem>
              )}
              {(tempAdmin || tempOwner || appUser) && (
                <MenuItem
                  icon={
                    <CircleDollarSign
                      size={17}
                      strokeWidth={1.5}
                      color={pathname === '/costAnalytics' ? activeColor : inactiveColor}
                    />
                  }
                  active={pathname === '/costAnalytics'}
                  component={<Link to="/costAnalytics" />}>
                  Cost Analytics
                </MenuItem>
              )}
              {(tempAdmin || tempOwner || appUser) && (
                <MenuItem
                  className="ethics-menu"
                  icon={
                    <img
                      src={model_library}
                      alt="System Analytics"
                      style={{
                        width: 16,
                        height: 16,
                        filter:
                          pathname === '/systemanalytics'
                            ? 'invert(27%) sepia(91%) saturate(2727%) hue-rotate(197deg) brightness(96%) contrast(96%)'
                            : 'none'
                      }}
                    />
                  }
                  active={pathname === '/systemanalytics'}
                  component={<Link to="/systemanalytics" />}>
                  <span>System Analytics</span>
                </MenuItem>
              )}

              {(tempAdmin || tempOwner || appUser) && (
                <MenuItem
                  className="ethics-menu"
                  icon={
                    <img
                      src={ethics}
                      alt="Ethics Compliance"
                      style={{
                        width: 16,
                        height: 16,
                        filter:
                          pathname === '/ethicscompliance'
                            ? 'invert(27%) sepia(91%) saturate(2727%) hue-rotate(197deg) brightness(96%) contrast(96%)'
                            : 'none'
                      }}
                    />
                  }
                  active={pathname === '/ethicscompliance'}
                  component={<Link to="/ethicscompliance" />}>
                  <span>Ethics & Compliance</span> <br /> Analytics
                </MenuItem>
              )}

              {(tempAdmin || tempOwner) && (
                <MenuItem
                  className="ethics-menu"
                  icon={
                    <img
                      src={model_library}
                      alt="Health Analytics"
                      style={{
                        width: 16,
                        height: 16,
                        filter:
                          pathname === '/healthdashboard'
                            ? 'invert(27%) sepia(91%) saturate(2727%) hue-rotate(197deg) brightness(96%) contrast(96%)'
                            : 'none'
                      }}
                    />
                  }
                  active={pathname === '/healthdashboard'}
                  component={<Link to="/healthdashboard" />}>
                  <span>Api Health Analytics </span>
                </MenuItem>
              )}
            </SubMenu>
          )}

          {/* RAG Builder */}
          {/* {!['localhost', 'aiflmdev.unilever.com']?.includes(window?.location?.hostname) && (
            <MenuItem
              icon={
                <Boxes
                  size={24}
                  strokeWidth={1.25}
                  color={activeItem === 'rag' ? activeColor : inactiveColor}
                />
              }
              onClick={() => {
                handleClick('rag');
                openRAGLink();
              }}
              active={activeItem === 'rag'}
              suffix={
                <ArrowUpRight
                  size={18}
                  strokeWidth={2}
                  color={activeItem === 'rag' ? activeColor : inactiveColor}
                />
              }>
              {collapsed ? '' : 'RAG Builder'}
            </MenuItem>
          )} */}

          {/* New Rag Builder */}
          {
            <SubMenu
              style={{ marginBottom: '2px' }}
              icon={
                <img
                  src={rag_icon}
                  alt="Rag Icon"
                  style={{
                    width: 24,
                    height: 24,
                    filter: isRagBuilderActive
                      ? 'invert(27%) sepia(91%) saturate(2727%) hue-rotate(197deg) brightness(96%) contrast(96%)'
                      : 'none'
                  }}
                />
              }
              label={
                <span style={{ color: isRagBuilderActive ? activeColor : inactiveColor }}>
                  {collapsed ? '' : 'RAG Builder'}
                </span>
              }
              onClick={() => handleSubMenuToggle('rag')}
              suffix={renderChevron('rag', isRagBuilderActive)}>
              <MenuItem
                icon={
                  <img
                    src={myrag_icon}
                    alt="myrag Icon"
                    style={{
                      width: 16,
                      height: 16,
                      filter:
                        pathname === '/ragbuilder'
                          ? 'invert(27%) sepia(91%) saturate(2727%) hue-rotate(197deg)'
                          : 'none'
                    }}
                  />
                }
                active={pathname === '/ragbuilder'}
                component={<Link to="/ragbuilder" />}>
                My RAG Apps
              </MenuItem>
              {/* <MenuItem
                icon={
                  <img
                    src={manage_rag_icon}
                    alt="manage Icon"
                    style={{
                      width: 16,
                      height: 16,
                      filter:
                        pathname === '/managerag'
                          ? 'invert(27%) sepia(91%) saturate(2727%) hue-rotate(197deg)'
                          : 'none'
                    }}
                  />
                }
                active={pathname === '/managerag'}
                component={<Link to="/managerag" />}>
                Manage All RAG Apps
              </MenuItem> */}
            </SubMenu>
          }
        </Menu>
      </Sidebar>

      {/* Collapse/Expand Button */}
      <button
        className={`collapse-btn ${collapsed ? 'collapsed' : 'expanded'}`}
        onClick={() => setCollapsed(!collapsed)}>
        <img
          src={collapsed ? expand : collapse}
          alt={collapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
          style={{ width: 20, height: 20 }}
        />
      </button>
    </div>
  );
};

export default SidebarComponent;
